// Users/Tenants management page
// Reference: spec.md US8 - owner/manager manages tenants

import { useState, useEffect } from 'react';
import { Button, Input, Select, FormField } from '../../components/ui';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import type { User, UserRole, Property, Unit } from '@propman/shared';

interface UserWithAssignments extends User {
  assignments: Array<{
    id: string;
    unitId: string;
    startDate: string;
    status: string;
    unit: {
      id: string;
      identifier: string;
      property: {
        id: string;
        name: string;
      };
    };
  }>;
}

interface PropertyWithUnits extends Property {
  units: Unit[];
}

const roleLabels: Record<UserRole, string> = {
  leaseholder: 'Tenant',
  manager: 'Manager',
  owner: 'Owner',
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';

  const [users, setUsers] = useState<UserWithAssignments[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [pendingPMs, setPendingPMs] = useState<User[]>([]);
  const [properties, setProperties] = useState<PropertyWithUnits[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInvitePMModal, setShowInvitePMModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignPMModal, setShowAssignPMModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter state
  const [filter, setFilter] = useState<'all' | 'pending' | 'pending-pms'>('all');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setIsLoading(true);
    setError(null);
    try {
      const requests: Promise<unknown>[] = [
        api.get<{ users: UserWithAssignments[] }>('/users'),
        api.get<{ users: User[] }>('/users/pending'),
        api.get<{ properties: PropertyWithUnits[] }>('/properties'),
      ];

      // Only owners can see pending PMs
      if (isOwner) {
        requests.push(api.get<{ users: User[] }>('/users/pending-pms'));
      }

      const results = await Promise.all(requests);
      const [usersRes, pendingRes, propertiesRes] = results as [
        { users: UserWithAssignments[] },
        { users: User[] },
        { properties: PropertyWithUnits[] }
      ];

      setUsers(usersRes.users);
      setPendingUsers(pendingRes.users);
      setProperties(propertiesRes.properties);

      if (isOwner && results[3]) {
        setPendingPMs((results[3] as { users: User[] }).users);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function loadPropertyUnits(propertyId: string) {
    try {
      const response = await api.get<{ units: Unit[] }>('/units', { propertyId });
      setProperties(prev => prev.map(p =>
        p.id === propertyId ? { ...p, units: response.units } : p
      ));
    } catch (err) {
      console.error('Failed to load units:', err);
    }
  }

  function openInviteModal() {
    setInviteEmail('');
    setInviteName('');
    setSelectedPropertyId('');
    setSelectedUnitId('');
    setFormError(null);
    setShowInviteModal(true);
  }

  function openInvitePMModal() {
    setInviteEmail('');
    setInviteName('');
    setSelectedPropertyIds([]);
    setFormError(null);
    setShowInvitePMModal(true);
  }

  function openAssignModal(user: User) {
    setSelectedUser(user);
    setSelectedPropertyId('');
    setSelectedUnitId('');
    setFormError(null);
    setShowAssignModal(true);
  }

  function openAssignPMModal(user: User) {
    setSelectedUser(user);
    setSelectedPropertyIds([]);
    setFormError(null);
    setShowAssignPMModal(true);
  }

  async function handlePropertyChange(propertyId: string) {
    setSelectedPropertyId(propertyId);
    setSelectedUnitId('');
    if (propertyId) {
      await loadPropertyUnits(propertyId);
    }
  }

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const availableUnits = selectedProperty?.units || [];

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setFormError(null);

    try {
      await api.post('/users/invite', {
        email: inviteEmail,
        name: inviteName,
        unitId: selectedUnitId,
      });
      await loadData();
      setShowInviteModal(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFormError(error.message || 'Failed to invite user');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUser) return;

    setIsSaving(true);
    setFormError(null);

    try {
      await api.post(`/users/${selectedUser.id}/assign`, {
        unitId: selectedUnitId,
      });
      await loadData();
      setShowAssignModal(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFormError(error.message || 'Failed to assign user');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleEndAssignment(userId: string, assignmentId: string) {
    if (!confirm('Are you sure you want to end this assignment?')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}/assignments/${assignmentId}`);
      await loadData();
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to end assignment');
    }
  }

  async function handleDeactivateUser(user: User) {
    if (!confirm(`Are you sure you want to deactivate "${user.name}"? They will no longer be able to access the system.`)) {
      return;
    }

    try {
      await api.delete(`/users/${user.id}`);
      await loadData();
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to deactivate user');
    }
  }

  async function handleInvitePM(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setFormError(null);

    try {
      await api.post('/users/invite-pm', {
        email: inviteEmail,
        name: inviteName,
        propertyIds: selectedPropertyIds,
      });
      await loadData();
      setShowInvitePMModal(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFormError(error.message || 'Failed to invite property manager');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAssignPM(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUser) return;

    setIsSaving(true);
    setFormError(null);

    try {
      await api.post(`/users/${selectedUser.id}/assign-properties`, {
        propertyIds: selectedPropertyIds,
      });
      await loadData();
      setShowAssignPMModal(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFormError(error.message || 'Failed to assign property manager');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleEndPMAssignment(userId: string, propertyId: string) {
    if (!confirm('Are you sure you want to remove this property assignment?')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}/property-assignments/${propertyId}`);
      await loadData();
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to remove property assignment');
    }
  }

  function togglePropertySelection(propertyId: string) {
    setSelectedPropertyIds(prev =>
      prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  }

  const displayUsers = filter === 'pending' ? pendingUsers : filter === 'pending-pms' ? pendingPMs : users;

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage tenants, property managers, and their assignments
          </p>
        </div>
        <div className="flex gap-2">
          {isOwner && (
            <Button variant="secondary" onClick={openInvitePMModal}>
              <svg className="w-5 h-5 mr-2 -ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Invite Manager
            </Button>
          )}
          <Button onClick={openInviteModal}>
            <svg className="w-5 h-5 mr-2 -ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Invite Tenant
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            filter === 'all'
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          All Users ({users.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            filter === 'pending'
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Pending Tenants ({pendingUsers.length})
        </button>
        {isOwner && (
          <button
            onClick={() => setFilter('pending-pms')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'pending-pms'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Pending Managers ({pendingPMs.length})
          </button>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="secondary" size="sm" className="mt-2" onClick={loadData}>
            Try Again
          </Button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && displayUsers.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {filter === 'pending' && 'No pending tenants'}
            {filter === 'pending-pms' && 'No pending managers'}
            {filter === 'all' && 'No users yet'}
          </h3>
          <p className="mt-2 text-gray-600">
            {filter === 'pending' && 'All tenants have been assigned to units.'}
            {filter === 'pending-pms' && 'No property managers waiting for assignment.'}
            {filter === 'all' && 'Invite tenants or managers to get started.'}
          </p>
          {filter === 'all' && (
            <Button className="mt-4" onClick={openInviteModal}>
              Invite Tenant
            </Button>
          )}
        </div>
      )}

      {/* Users list */}
      {!isLoading && displayUsers.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {(filter === 'pending' || filter === 'pending-pms') ? 'Status' : 'Assignments'}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filter === 'pending' ? (
                pendingUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {roleLabels[user.role]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Waiting for Assignment
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button size="sm" onClick={() => openAssignModal(user)}>
                        Assign to Unit
                      </Button>
                    </td>
                  </tr>
                ))
              ) : filter === 'pending-pms' ? (
                pendingPMs.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-700">
                            {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {roleLabels[user.role]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Waiting for Property Assignment
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button size="sm" onClick={() => openAssignPMModal(user)}>
                        Assign to Properties
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'owner' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {roleLabels[user.role]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.assignments && user.assignments.length > 0 ? (
                        <div className="space-y-1">
                          {user.assignments.filter(a => a.status === 'active').map((assignment) => (
                            <div key={assignment.id} className="flex items-center justify-between text-sm">
                              <span className="text-gray-900">
                                {assignment.unit.property.name} - {assignment.unit.identifier}
                              </span>
                              {user.role === 'leaseholder' && (
                                <button
                                  type="button"
                                  onClick={() => handleEndAssignment(user.id, assignment.id)}
                                  className="text-red-600 hover:text-red-700 text-xs ml-2"
                                >
                                  End
                                </button>
                              )}
                            </div>
                          ))}
                          {/* Property manager assignments */}
                          {user.assignments.filter(a => a.status === 'property_manager').map((assignment) => (
                            <div key={assignment.unit.property.id} className="flex items-center justify-between text-sm">
                              <span className="text-gray-900">
                                {assignment.unit.property.name} <span className="text-blue-600 text-xs">(Manager)</span>
                              </span>
                              {isOwner && user.role === 'manager' && (
                                <button
                                  type="button"
                                  onClick={() => handleEndPMAssignment(user.id, assignment.unit.property.id)}
                                  className="text-red-600 hover:text-red-700 text-xs ml-2"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No active assignments</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {user.role === 'leaseholder' && (
                          <Button variant="secondary" size="sm" onClick={() => openAssignModal(user)}>
                            Assign
                          </Button>
                        )}
                        {isOwner && user.role === 'manager' && (
                          <Button variant="secondary" size="sm" onClick={() => openAssignPMModal(user)}>
                            Assign Properties
                          </Button>
                        )}
                        {user.role === 'leaseholder' && user.isActive && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeactivateUser(user)}
                          >
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <Modal title="Invite Tenant" onClose={() => setShowInviteModal(false)}>
          <form onSubmit={handleInvite} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <FormField label="Email" htmlFor="invite-email" required>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="tenant@example.com"
                required
              />
            </FormField>

            <FormField label="Name" htmlFor="invite-name" required>
              <Input
                id="invite-name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </FormField>

            <FormField label="Property" htmlFor="invite-property" required>
              <Select
                id="invite-property"
                value={selectedPropertyId}
                onChange={(e) => handlePropertyChange(e.target.value)}
                required
              >
                <option value="">Select a property</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>{property.name}</option>
                ))}
              </Select>
            </FormField>

            {selectedPropertyId && (
              <FormField label="Unit" htmlFor="invite-unit" required>
                <Select
                  id="invite-unit"
                  value={selectedUnitId}
                  onChange={(e) => setSelectedUnitId(e.target.value)}
                  required
                >
                  <option value="">Select a unit</option>
                  {availableUnits.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.identifier}{unit.floor ? ` (Floor ${unit.floor})` : ''}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setShowInviteModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving} disabled={!selectedUnitId}>
                Send Invite
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedUser && (
        <Modal title={`Assign ${selectedUser.name}`} onClose={() => setShowAssignModal(false)}>
          <form onSubmit={handleAssign} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <p className="text-sm text-gray-600">
              Assign this user to a unit. They will be able to report issues for the assigned unit.
            </p>

            <FormField label="Property" htmlFor="assign-property" required>
              <Select
                id="assign-property"
                value={selectedPropertyId}
                onChange={(e) => handlePropertyChange(e.target.value)}
                required
              >
                <option value="">Select a property</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>{property.name}</option>
                ))}
              </Select>
            </FormField>

            {selectedPropertyId && (
              <FormField label="Unit" htmlFor="assign-unit" required>
                <Select
                  id="assign-unit"
                  value={selectedUnitId}
                  onChange={(e) => setSelectedUnitId(e.target.value)}
                  required
                >
                  <option value="">Select a unit</option>
                  {availableUnits.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.identifier}{unit.floor ? ` (Floor ${unit.floor})` : ''}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setShowAssignModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving} disabled={!selectedUnitId}>
                Assign User
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Invite PM Modal */}
      {showInvitePMModal && (
        <Modal title="Invite Property Manager" onClose={() => setShowInvitePMModal(false)}>
          <form onSubmit={handleInvitePM} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <FormField label="Email" htmlFor="invite-pm-email" required>
              <Input
                id="invite-pm-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="manager@example.com"
                required
              />
            </FormField>

            <FormField label="Name" htmlFor="invite-pm-name" required>
              <Input
                id="invite-pm-name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </FormField>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Properties to Manage <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Select one or more properties this manager will be able to manage.
              </p>
              <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                {properties.map((property) => (
                  <label
                    key={property.id}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPropertyIds.includes(property.id)}
                      onChange={() => togglePropertySelection(property.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-900">{property.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setShowInvitePMModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving} disabled={selectedPropertyIds.length === 0}>
                Send Invite
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Assign PM to Properties Modal */}
      {showAssignPMModal && selectedUser && (
        <Modal title={`Assign Properties to ${selectedUser.name}`} onClose={() => setShowAssignPMModal(false)}>
          <form onSubmit={handleAssignPM} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <p className="text-sm text-gray-600">
              Select properties this manager will be able to manage. They will be able to view and manage issues, tenants, and units for these properties.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Properties to Manage <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                {properties.map((property) => (
                  <label
                    key={property.id}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPropertyIds.includes(property.id)}
                      onChange={() => togglePropertySelection(property.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-900">{property.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setShowAssignPMModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving} disabled={selectedPropertyIds.length === 0}>
                Assign Properties
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// Modal component
interface ModalProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}

function Modal({ title, children, onClose }: ModalProps) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default UsersPage;
